import { Directive, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account-data';
import { Analytics } from '@recruitee/analytics';
import { filterBoolean } from '@recruitee/common';
import { I18nService } from '@recruitee/i18n';
import { ViewType } from '@recruitee/search-ui';
import { DialogService, NotificationsController } from '@recruitee/user-interface';

import { ActionsVisibilityBreakpoint } from './bulk-actions-toolbar/bulk-actions-toolbar.component';
import {
  BulkAction,
  BulkActionType,
  BulkItemBase,
  GetBulkActionsProps,
} from './bulk-actions.types';

@Directive({ standalone: true })
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class BulkActionsController<BulkItem extends BulkItemBase = BulkItemBase>
  implements OnDestroy
{
  public selectedItems$: BehaviorSubject<BulkItem[]> = new BehaviorSubject([]);
  public active$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public selectedIdsDict$: BehaviorSubject<Record<number | string, true>> = new BehaviorSubject({});
  public ready$: Observable<boolean>;
  public recalculateActions$ = new Subject<void>();

  protected availableActions: BulkAction[];
  protected ngOnDestroy$ = new Subject<void>();

  private actionsInProgress: Partial<Record<BulkActionType, boolean>> = {};

  // eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
  protected constructor(
    protected notificationsCtrl: NotificationsController,
    protected store: Store,
    protected analytics: Analytics,
    protected dialogService: DialogService,
    protected i18n: I18nService,
  ) {
    this.ready$ = this.store.select(AccountSelectors.currentCompany).pipe(
      filterBoolean,
      first(),
      map(company => !!company),
      takeUntil(this.ngOnDestroy$),
    );

    this.selectedItems$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(items => {
      const selectedIds = items.reduce<Record<number, true>>((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {});
      this.selectedIdsDict$.next(selectedIds);
      if (items.length === 0) {
        this.active$.next(false);
      } else if (!this.active$.getValue()) {
        this.active$.next(true);
      }
    });

    this.active$
      .asObservable()
      .pipe(
        filter(v => !v && this.selectedItems$.getValue().length > 0),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => this.clearSelection());
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get buttons(): BulkAction[] {
    return this.availableActions;
  }

  public get active(): boolean {
    return this.active$.getValue();
  }

  public abstract getBulkActions(props: GetBulkActionsProps): BulkAction[];
  public abstract dispatchAction(data: {
    action: BulkAction;
    offer?: { kind: 'job' | 'talent_pool'; id: number } | null;
    customCandidates?: BulkItem[];
    requisition?: { id: number } | null;
  }): void;
  public abstract getActionsVisibilityByBreakpoint(
    viewType?: ViewType,
  ): ActionsVisibilityBreakpoint[];

  public isItemSelected(item: BulkItem): boolean {
    return this.selectedIdsDict$.getValue()[item.id];
  }

  public selectItem(item: BulkItem, toggle: boolean = !this.isItemSelected(item)): void {
    const nextSelectedItems = this.selectedItems$
      .getValue()
      .filter(i => i.id !== item.id)
      .concat(toggle ? [item] : []);
    this.selectedItems$.next(nextSelectedItems);
  }

  public selectItems(items: BulkItem[], toggle: boolean): void {
    const itemsIds = items.map(item => item.id);
    const nextSelectedItems = this.selectedItems$
      .getValue()
      .filter(i => !itemsIds.includes(i.id))
      .concat(toggle ? items : []);
    this.selectedItems$.next(nextSelectedItems);
  }

  public clearSelection(): void {
    this.selectedItems$.next([]);
  }

  public toggleActive(): void {
    this.active$.next(!this.active);
  }

  public isActionInProgress(action: BulkAction): boolean {
    return this.actionsInProgress[action.id]!;
  }

  protected markBulkActionAsInProgress(action: BulkAction): void {
    if (!this.isActionInProgress(action)) {
      this.actionsInProgress[action.id] = true;
    }
  }

  protected markBulkActionAsCompleted(action: BulkAction): void {
    this.actionsInProgress[action.id] = false;
  }

  protected dispatchActionError(action: BulkAction, error: any): void {
    console.error('error', action, error);
  }
}
